import { GPanelPermissionsModel } from '../../../types';
import { Dispatch, SetStateAction, useEffect } from 'react';

import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import ListItemText from '@mui/material/ListItemText';
import Lock from '@mui/icons-material/Lock';
import Public from '@mui/icons-material/Public';
import Domain from '@mui/icons-material/Domain';

interface Visibility {
  visibility: 'Private' | 'Domain' | 'Domain_With_Link' | 'Public_With_Link';
  role: string;
}

interface DriveFileVisibilityListItemProps {
  permissions: '' | GPanelPermissionsModel | undefined;
  primaryDomain: string;
  setVisibility: Dispatch<SetStateAction<Visibility>>;
}

export default function DriveFileVisibilityListItem({
  permissions,
  primaryDomain,
  setVisibility,
}: DriveFileVisibilityListItemProps) {
  useEffect(() => {
    if (permissions === '') {
      setVisibility({ visibility: 'Private', role: 'reader' });
    } else if (permissions?.userEmail === 'anyone') {
      setVisibility({
        visibility: 'Public_With_Link',
        role: permissions.role,
      });
    } else if (
      permissions?.userEmail === 'domain' &&
      !permissions.allowFileDiscovery
    ) {
      setVisibility({
        visibility: 'Domain_With_Link',
        role: permissions.role,
      });
    } else if (
      permissions?.userEmail === 'domain' &&
      permissions.allowFileDiscovery
    ) {
      setVisibility({ visibility: 'Domain', role: permissions.role });
    }
  }, [permissions, setVisibility]);

  if (permissions === '') {
    return (
      <ListItem dense>
        <ListItemAvatar>
          <Avatar>
            <Lock />
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary="Restricted"
          secondary="Only people with access can open with the link"
        />
      </ListItem>
    );
  } else if (permissions?.userEmail === 'anyone') {
    return (
      <ListItem dense>
        <ListItemAvatar>
          <Avatar>
            <Public />
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary="Anyone with the link"
          secondary={`Anyone on the internet with the link can ${
            permissions.role === 'reader'
              ? 'view'
              : permissions.role === 'commenter'
              ? 'comment'
              : permissions.role === 'writer'
              ? 'edit'
              : 'view'
          }`}
        />
      </ListItem>
    );
  } else if (
    permissions?.userEmail === 'domain' &&
    !permissions.allowFileDiscovery
  ) {
    return (
      <ListItem dense>
        <ListItemAvatar>
          <Avatar>
            <Domain />
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={primaryDomain}
          secondary={`Anyone at ${primaryDomain} with the link can ${
            permissions.role === 'reader'
              ? 'view'
              : permissions.role === 'commenter'
              ? 'comment'
              : permissions.role === 'writer'
              ? 'edit'
              : 'view'
          }`}
        />
      </ListItem>
    );
  } else if (
    permissions?.userEmail === 'domain' &&
    permissions.allowFileDiscovery
  ) {
    return (
      <ListItem dense>
        <ListItemAvatar>
          <Avatar>
            <Domain />
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={primaryDomain}
          secondary={`Anyone at ${primaryDomain} can find and ${
            permissions.role === 'reader'
              ? 'view'
              : permissions.role === 'commenter'
              ? 'comment'
              : permissions.role === 'writer'
              ? 'edit'
              : 'view'
          }`}
        />
      </ListItem>
    );
  } else {
    return <></>;
  }
}
